import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  Minus,
  ArrowLeft,
  Check,
  AlertTriangle,
  FlaskConical,
  Truck,
  ShieldCheck,
  BadgeCheck,
  FileCheck,
  Microscope,
  Package,
} from "lucide-react";
import { fetchProduct, fetchPairings } from "../lib/api";
import { track } from "../lib/pixel";
import { useCart } from "../context/CartContext";
import { StarRating } from "../components/StarRating";
import { Reveal } from "../components/Reveal";
import { DosageCalculator } from "../components/DosageCalculator";
import { CoaModal } from "../components/CoaModal";
import { money } from "../lib/api";
import { toast } from "sonner";

export default function ProductDetail() {
  const { handle } = useParams();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [showCoa, setShowCoa] = useState(false);
  const [variantIdx, setVariantIdx] = useState(0);
  const [pairings, setPairings] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    setQty(1);
    setActiveImg(0);
    fetchProduct(handle)
      .then((p) => {
        setProduct(p);
        setVariantIdx(p.default_variant || 0);
        track("ViewContent", { content_name: p.name, content_ids: [p.handle], value: p.price, currency: "AUD" });
        fetchPairings([p.handle]).then(setPairings).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [handle]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl">Product not found</p>
        <Link to="/shop" className="text-accent font-mono-tech uppercase text-sm tracking-widest">
          ← Back to shop
        </Link>
      </div>
    );

  if (!product)
    return <div className="min-h-screen pt-20 max-w-[1400px] mx-auto px-5 md:px-10 animate-pulse" />;

  const variants = product.variants || [];
  const sel = variants[variantIdx] || { label: product.dose, price: product.price, compare_at: product.compare_at };
  const mainImg = activeImg > 0
    ? product.images[activeImg]
    : (variants.length > 1 && sel.image ? sel.image : product.images[0]);

  const discount =
    sel.compare_at && sel.compare_at > sel.price
      ? Math.round((1 - sel.price / sel.compare_at) * 100)
      : null;

  const add = () => {
    addItem({ ...product, images: [sel.image || product.images[0]] }, qty, variants.length ? sel.label : null);
    toast.success(`${product.name} ${variants.length ? sel.label + " " : ""}added to cart`);
  };
  const buyNow = () => {
    addItem({ ...product, images: [sel.image || product.images[0]] }, qty, variants.length ? sel.label : null);
    setOpen(true);
  };

  return (
    <div className="pt-24 md:pt-28">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-accent transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </div>

      {/* MAIN */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 pb-20">
        {/* gallery */}
        <div className="lg:sticky lg:top-24 self-start">
          <motion.div
            key={activeImg}
            initial={{ opacity: 0.4, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative aspect-square bg-ink-800 border border-[#27272A] overflow-hidden clip-corner-lg"
          >
            <img
              src={mainImg}
              alt={product.name}
              className={`h-full w-full object-cover ${!product.in_stock ? "grayscale brightness-[0.4]" : ""}`}
              data-testid="product-main-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/40 to-transparent pointer-events-none" />
            {discount && product.in_stock && (
              <span className="absolute top-4 left-4 bg-accent text-white font-mono-tech text-xs font-bold tracking-widest px-3 py-1.5">
                SAVE {discount}%
              </span>
            )}
            {!product.in_stock && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink-900/50" data-testid="sold-out-overlay">
                <span className="font-display font-black text-3xl sm:text-4xl tracking-[0.2em] uppercase text-accent border-[3px] border-accent bg-ink-900/85 backdrop-blur-sm px-8 py-4 -rotate-12 shadow-[0_0_50px_rgba(255,51,31,0.4)]">
                  Sold Out
                </span>
              </div>
            )}
          </motion.div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 overflow-hidden border transition-colors ${
                    activeImg === i ? "border-accent" : "border-[#27272A] hover:border-white/40"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {product.goals.map((g) => (
              <span
                key={g}
                className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-accent border border-accent/40 px-2.5 py-1"
              >
                {g}
              </span>
            ))}
            {product.is_bestseller && product.in_stock && (
              <span className="bg-accent text-white font-display font-black text-xs tracking-widest px-3 py-1.5 shadow-[0_0_24px_rgba(255,51,31,0.55)]" data-testid="bestseller-badge">
                BEST SELLER
              </span>
            )}
            {product.in_stock ? (
              <span className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-emerald-400">
                <span className="h-1.5 w-1.5 bg-emerald-400 rounded-full" /> In Stock
              </span>
            ) : (
              <span className="flex items-center gap-1.5 font-mono-tech text-sm uppercase tracking-widest text-accent font-bold border border-accent/60 bg-accent/10 px-3 py-1.5" data-testid="sold-out-badge">
                <span className="h-2 w-2 bg-accent rounded-full" /> Sold Out — Unavailable
              </span>
            )}
          </div>

          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl leading-[0.9]">
            {product.name}
          </h1>
          <p className="font-mono-tech text-sm tracking-[0.2em] uppercase text-zinc-500 mt-2">
            {sel.label} · {product.subtitle}
          </p>

          <div className="flex items-center gap-3 mt-5">
            <StarRating rating={product.rating} size={16} />
            <span className="font-mono-tech text-xs text-zinc-400">
              {product.rating} ({product.review_count} verified reviews)
            </span>
          </div>

          {variants.length > 1 && (
            <div className="mt-7" data-testid="variant-selector">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Select size
              </p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => {
                  const soldOut = v.in_stock === false;
                  return (
                  <button
                    key={v.label}
                    onClick={() => { if (!soldOut) { setVariantIdx(i); setActiveImg(0); } }}
                    disabled={soldOut}
                    className={`border px-5 py-3 font-mono-tech text-sm transition-all ${
                      soldOut
                        ? "border-[#27272A] text-zinc-600 cursor-not-allowed opacity-60"
                        : i === variantIdx
                        ? "border-accent bg-accent/10 text-white"
                        : "border-[#27272A] text-zinc-400 hover:border-zinc-500"
                    }`}
                    data-testid={`variant-${v.label}`}
                  >
                    <span className={`font-bold ${soldOut ? "line-through" : ""}`}>{v.label}</span>
                    {!soldOut && i === (product.default_variant || 0) && variants.length > 1 && (
                      <span className={`ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${i === variantIdx ? "bg-accent text-white" : "bg-accent/20 text-accent"}`}>
                        Recommended
                      </span>
                    )}
                    {soldOut ? (
                      <span className="text-accent ml-2 text-[10px] uppercase tracking-widest font-bold">Sold out</span>
                    ) : (
                      <span className={i === variantIdx ? "text-accent ml-2" : "text-zinc-500 ml-2"}>
                        {money(v.price)}
                      </span>
                    )}
                  </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-baseline gap-4 mt-7">
            <span className="font-display font-black text-4xl" data-testid="product-price">{money(sel.price)}</span>
            {sel.compare_at && (
              <span className="font-mono-tech text-lg text-zinc-600 line-through">
                {money(sel.compare_at)}
              </span>
            )}
            <span className="font-mono-tech text-[11px] text-zinc-500 uppercase tracking-widest">
              AUD · incl. tax at checkout
            </span>
          </div>

          {product.is_new && sel.compare_at && sel.compare_at > sel.price && (
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="deal-badges">
              <span className="bg-white text-ink-900 font-mono-tech text-[10px] font-bold tracking-widest px-2.5 py-1 shadow-[0_0_16px_rgba(255,255,255,0.25)]" data-testid="new-badge">
                NEW LAUNCH
              </span>
              <span className="bg-accent/15 border border-accent text-accent font-mono-tech text-[10px] font-bold tracking-widest px-2.5 py-1" data-testid="deal-badge">
                LAUNCH DEAL — SAVE {money(sel.compare_at - sel.price)} ({Math.round((1 - sel.price / sel.compare_at) * 100)}% OFF)
              </span>
            </div>
          )}

          <p className="text-zinc-300 text-base leading-relaxed mt-7">{product.summary}</p>

          {/* specs */}
          <div className="grid grid-cols-2 gap-px bg-[#27272A] border border-[#27272A] mt-8">
            {product.specs.map((s, i) => (
              <div key={i} className="bg-ink-800 px-4 py-3 flex items-center gap-2">
                <Check size={14} className="text-accent shrink-0" />
                <span className="font-mono-tech text-xs text-zinc-300">{s}</span>
              </div>
            ))}
          </div>

          {/* actions */}
          <button
            onClick={() => setShowCoa(true)}
            className="flex items-center gap-2 mt-4 font-mono-tech text-[11px] uppercase tracking-widest text-accent hover:text-white transition-colors"
            data-testid="view-coa-button"
          >
            <FileCheck size={15} /> View Certificate of Analysis (COA)
          </button>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            {!product.in_stock ? (
              <div
                className="flex-1 h-14 border border-accent/50 bg-accent/5 flex items-center justify-center font-display font-bold uppercase tracking-wide text-base text-accent clip-corner"
                data-testid="sold-out-block"
              >
                Sold Out — Back Soon
              </div>
            ) : (
              <>
                <div className="flex items-center border border-[#27272A] h-14">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="h-full w-12 flex items-center justify-center text-zinc-400 hover:text-accent"
                    data-testid="qty-dec"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-10 text-center font-mono-tech" data-testid="qty-value">
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty((q) => q + 1)}
                    className="h-full w-12 flex items-center justify-center text-zinc-400 hover:text-accent"
                    data-testid="qty-inc"
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <button
                  onClick={add}
                  className="flex-1 h-14 border border-white/25 hover:border-white text-white font-display font-bold uppercase tracking-wide text-base transition-colors"
                  data-testid="add-to-cart-button"
                >
                  Add to Cart
                </button>
                <button
                  onClick={buyNow}
                  className="flex-1 h-14 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide text-base transition-colors clip-corner"
                  data-testid="buy-now-button"
                >
                  Buy Now
                </button>
              </>
            )}
          </div>

          {/* trust mini */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 mt-7">
            {[
              [FlaskConical, "Lab tested > 99%"],
              [Truck, "Free shipping over $250"],
              [ShieldCheck, "Secure encrypted checkout"],
            ].map(([Icon, t], i) => (
              <span key={i} className="flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-wider text-zinc-400">
                <Icon size={14} className="text-accent" /> {t}
              </span>
            ))}
          </div>

          {product.in_stock && (
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-accent mt-5" data-testid="bundle-nudge">
              Bundle any 2+ items — save 15% automatically at checkout
            </p>
          )}

          {/* research disclaimer */}
          <div className="border border-accent/40 bg-accent/5 p-4 clip-corner mt-8">
            <div className="flex gap-2 text-accent mb-2">
              <AlertTriangle size={15} />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">
                Research use only
              </span>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              For laboratory and research purposes only. Not for human consumption. Not a therapeutic
              good. You must be 18+ to purchase.
            </p>
          </div>
        </div>
      </div>

      {/* DESCRIPTION + HIGHLIGHTS */}
      <section className="border-t border-[#27272A] bg-ink-800/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
              / The research
            </span>
            <h2 className="font-display font-black tracking-tighter text-4xl mt-3 mb-6 leading-none">
              ABOUT {product.name.toUpperCase()}
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed whitespace-pre-line">
              {product.description}
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <h3 className="font-mono-tech text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
              Research highlights
            </h3>
            <ul className="space-y-4">
              {product.highlights.map((h, i) => (
                <li key={i} className="flex gap-3 border-b border-[#27272A] pb-4">
                  <BadgeCheck size={20} className="text-accent shrink-0 mt-0.5" />
                  <span className="text-zinc-200 leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* RESEARCH PROFILE — mechanism, studied applications, handling */}
      {product.research && (
        <section className="border-t border-[#27272A]" data-testid="research-profile">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-7">
              <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                / Mechanism
              </span>
              <h2 className="font-display font-black tracking-tighter text-4xl mt-3 mb-6 leading-none">
                HOW IT'S STUDIED
              </h2>
              <p className="text-zinc-300 text-lg leading-relaxed">{product.research.mechanism}</p>
              <div className="mt-8 border border-[#27272A] bg-ink-800/60 p-5 clip-corner">
                <div className="flex items-center gap-2 mb-2">
                  <Package size={15} className="text-accent" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
                    Storage & handling
                  </span>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">{product.research.storage}</p>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={0.1}>
              <h3 className="font-mono-tech text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
                Studied applications
              </h3>
              <ul className="space-y-4">
                {product.research.studied.map((s, i) => (
                  <li key={i} className="flex gap-3 border-b border-[#27272A] pb-4">
                    <Microscope size={20} className="text-accent shrink-0 mt-0.5" />
                    <span className="text-zinc-200 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      {/* COMPLETE THE PROTOCOL — quick-add pairings */}
      {pairings.length > 0 && (
        <section className="border-t border-[#27272A] bg-ink-800/30" data-testid="product-pairings">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
            <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                  / Stack it
                </span>
                <h2 className="font-display font-black tracking-tighter text-4xl mt-3 leading-none">
                  COMPLETE THE PROTOCOL
                </h2>
              </div>
              <p className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-500">
                Any 2+ items = 15% off · 4+ = 20% · 10+ = 30%
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-4">
              {pairings.map((p) => (
                <Reveal key={p.handle} className="border border-[#27272A] bg-ink-900 p-4 clip-corner flex flex-col" data-testid={`product-pairing-${p.handle}`}>
                  <Link to={`/product/${p.handle}`} className="block h-40 bg-ink-700 overflow-hidden mb-4">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
                  </Link>
                  <p className="font-display font-bold text-lg leading-tight">{p.name}</p>
                  <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    {p.dose} · {money(p.price)}
                  </p>
                  <button
                    onClick={() => {
                      addItem({ ...p, images: [p.image] }, 1);
                      toast.success(`${p.name} added — bundle saving applied at checkout`);
                    }}
                    className="mt-4 border border-accent text-accent hover:bg-accent hover:text-white transition-colors px-4 py-2.5 font-mono-tech text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 clip-corner"
                    data-testid={`product-pairing-add-${p.handle}`}
                  >
                    <Plus size={14} /> Quick Add
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DOSAGE CALCULATOR */}
      {product.recon && (
        <section className="border-t border-[#27272A]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
            <Reveal className="mb-10 max-w-2xl">
              <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
                / Protocol tools
              </span>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
                DIAL IN YOUR PROTOCOL
              </h2>
              <p className="text-zinc-400 mt-4 leading-relaxed">
                Work out concentration, draw volume and doses per vial for your {product.name} research
                protocol. Adjust the inputs to match your setup.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <DosageCalculator recon={product.recon} productName={product.name} />
            </Reveal>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20" data-testid="reviews-section">        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
              / Verified reviews
            </span>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-none">
              WHAT RESEARCHERS SAY
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-display font-black text-6xl leading-none">{product.rating}</span>
            <div>
              <StarRating rating={product.rating} size={18} />
              <p className="font-mono-tech text-xs text-zinc-500 mt-1">
                {product.review_count} reviews
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {product.reviews.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="bg-ink-800 border border-[#27272A] p-6 h-full flex flex-col clip-corner">
                <div className="flex items-center justify-between mb-3">
                  <StarRating rating={r.rating} size={14} />
                  {r.verified && (
                    <span className="flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-widest text-emerald-400">
                      <Check size={11} /> Verified
                    </span>
                  )}
                </div>
                <h4 className="font-display font-bold text-lg leading-tight">{r.title}</h4>
                <p className="text-sm text-zinc-400 leading-relaxed mt-2 flex-1">{r.body}</p>
                <div className="mt-5 pt-4 border-t border-[#27272A] flex items-center gap-3">
                  {r.avatar && (
                    <img
                      src={r.avatar}
                      alt={r.name}
                      className="h-9 w-9 rounded-full object-cover border border-[#27272A]"
                      loading="lazy"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-mono-tech text-[11px] text-zinc-200 block leading-none">
                      {r.name}
                    </span>
                    <span className="font-mono-tech text-[10px] text-zinc-600 uppercase tracking-wider">
                      {r.location}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* packaging & shipping disclaimer */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pb-16" data-testid="packaging-disclaimer">
        <div className="border-t border-[#27272A] pt-6">
          <p className="font-mono-tech text-[11px] leading-relaxed text-zinc-500 max-w-3xl">
            Vial packaging and presentation are subject to change depending on availability — same trusted
            quality and purity in every batch. All orders ship tracked within 7–14 business days
            (free over $250, otherwise $15 flat). Products are supplied for laboratory research use only.
          </p>
        </div>
      </section>

      <CoaModal coa={product.coa} open={showCoa} onClose={() => setShowCoa(false)} />
    </div>
  );
}
